/**
 * Auth0 SSO for the ops dashboard. Public surface used by the rest of the app.
 *
 * Flow (mirrors platform-core-iac):
 *   1. `redirectToLogin()` → BFF `/Auth0/User/Authorize?state=…&redirect_uri=…`
 *   2. BFF bounces through Auth0, then back to `/auth/callback#access_token=…`
 *   3. `processAuthCallback()` stores the token in the shared `.signatureaviation.com`
 *      cookie; the user lands back on their original page, now authenticated.
 *   4. `logout()` clears the cookie and hits Auth0's `/v2/logout`.
 */
import { jwtDecode } from "jwt-decode";
import {
  AUTH_LOGIN_URL,
  AUTH0_CLIENT_ID,
  AUTH0_DOMAIN,
  AUTH_RETURN_PATH_KEY,
  AUTH_STATE_KEY,
  SKYAGENT_HANDOFF_URL,
  SKYAGENT_URL,
  getAuthMode,
  isAuthDisabled,
  getCallbackUrl,
} from "./config";
import {
  clearTokenCookie,
  getTokenCookie,
  parseHashParams,
  setTokenCookie,
} from "./cookies";
import { resetHandoffGuard } from "@/lib/api-client";

export { getTokenCookie } from "./cookies";
export {
  AUTH_MODE,
  type AuthMode,
  getAuthMode,
  isAuthDisabled,
  setRuntimeAuthMode,
} from "./config";
export { AUTH_STATE_KEY, SKYAGENT_HANDOFF_URL, SKYAGENT_URL } from "./config";
export { loginWithPassword } from "./simple";

/** Subset of token claims the dashboard reads. */
export interface AuthClaims {
  sub: string;
  exp?: number;
  email?: string;
  name?: string;
  picture?: string;
  /** Auth0 injects an array; the simple-auth session token uses a single string. */
  role?: string[] | string;
  [key: string]: unknown;
}

export interface AuthUser {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  roles: string[];
}

const DEV_USER: AuthUser = {
  sub: "dev-user",
  email: "dev@local",
  name: "Local Dev",
  roles: ["Administrator"],
};

/** Decode (without verifying — the API verifies) and reject if expired. */
export function decodeToken(token: string): AuthClaims | null {
  try {
    const claims = jwtDecode<AuthClaims>(token);
    if (typeof claims.exp === "number" && claims.exp * 1000 <= Date.now()) {
      return null;
    }
    return claims;
  } catch {
    return null;
  }
}

export function claimsToUser(c: AuthClaims): AuthUser {
  const roles = Array.isArray(c.role) ? c.role : c.role ? [c.role] : [];
  return {
    sub: c.sub,
    email: c.email,
    name: c.name,
    picture: c.picture,
    roles,
  };
}

/** The current signed-in user, or null. In disabled mode, a synthetic admin. */
export function getCurrentUser(): AuthUser | null {
  if (isAuthDisabled()) return DEV_USER;
  const token = getTokenCookie();
  if (!token) return null;
  const claims = decodeToken(token);
  return claims ? claimsToUser(claims) : null;
}

/** Build the BFF login URL carrying the return path + our callback origin. */
export function getLoginUrlWithState(returnPath = "/home"): string {
  if (!AUTH_LOGIN_URL) {
    console.error("VITE_AUTH_LOGIN_URL is not configured");
    return "/";
  }
  const params = new URLSearchParams({ state: returnPath });
  const callback = getCallbackUrl();
  if (callback) params.append("redirect_uri", callback);
  const sep = AUTH_LOGIN_URL.includes("?") ? "&" : "?";
  return `${AUTH_LOGIN_URL}${sep}${params.toString()}`;
}

/**
 * Send the user to sign in. In `simple` mode that's our own `/login` form; in
 * `auth0` mode it's the Auth0 redirect (via the BFF); in `skyagent` mode it's
 * the SkyAgent handoff endpoint. Remembers the return path.
 */
export function redirectToLogin(returnPath?: string): void {
  if (typeof window === "undefined") return;
  const path = returnPath ?? window.location.pathname + window.location.search;
  try {
    sessionStorage.setItem(AUTH_RETURN_PATH_KEY, path);
  } catch {
    /* sessionStorage may be blocked (private mode) — the `state`/query param covers us. */
  }
  if (getAuthMode() === "simple") {
    const q = isValidRedirectUrl(path) ? `?returnTo=${encodeURIComponent(path)}` : "";
    window.location.href = `/login${q}`;
    return;
  }
  if (getAuthMode() === "skyagent") {
    // Generate a CSRF state nonce and persist it.
    const nonce = crypto.randomUUID();
    try {
      sessionStorage.setItem(AUTH_STATE_KEY, nonce);
    } catch {
      /* sessionStorage blocked — proceed without nonce; the callback will fail
         state validation (fail closed). */
    }
    const params = new URLSearchParams({
      redirect_uri: getCallbackUrl(),
      state: nonce,
    });
    const sep = SKYAGENT_HANDOFF_URL.includes("?") ? "&" : "?";
    window.location.assign(`${SKYAGENT_HANDOFF_URL}${sep}${params.toString()}`);
    return;
  }
  window.location.href = getLoginUrlWithState(path);
}

/** Only allow same-origin, path-style redirects (defends against open-redirect). */
export function isValidRedirectUrl(url: string | null | undefined): url is string {
  return typeof url === "string" && url.startsWith("/") && !url.startsWith("//");
}

export interface CallbackResult {
  success: boolean;
  redirectPath: string;
  error?: string;
}

/**
 * Handle `/auth/callback`: pull the token from the URL hash, store it in the
 * shared cookie, and compute where to send the user next.
 *
 * In skyagent mode the `state` parameter is a CSRF nonce that must match the
 * one stored in sessionStorage. A mismatch (or missing stored nonce) means the
 * callback is rejected — the token is NOT stored.
 */
export function processAuthCallback(): CallbackResult {
  if (typeof window === "undefined") {
    return { success: false, redirectPath: "/", error: "No window" };
  }

  const hashParams = parseHashParams(window.location.hash);

  // Surface errors from the IdP (e.g. user denied, handoff failed).
  if (hashParams.error) {
    removeStateNonce();
    return {
      success: false,
      redirectPath: "/",
      error: hashParams.error_description ?? hashParams.error,
    };
  }

  // An already-present cookie (SSO from another app) is enough on its own —
  // but only when there is no hash (re-entry / refresh). When a hash IS present
  // we must process it (which may include skyagent state validation).
  const tokenFromHash = hashParams.access_token ?? hashParams.id_token;
  const token = tokenFromHash ?? getTokenCookie();

  if (!token) {
    removeStateNonce();
    return { success: false, redirectPath: "/", error: "No token in callback" };
  }

  // In skyagent mode, validate the state nonce when a hash token is present.
  if (tokenFromHash && getAuthMode() === "skyagent") {
    const storedNonce = getStateNonce();
    const returnedState = hashParams.state ?? "";

    if (!storedNonce || returnedState !== storedNonce) {
      removeStateNonce();
      return {
        success: false,
        redirectPath: "/",
        error: "State mismatch — possible CSRF attack",
      };
    }
  }

  const claims = decodeToken(token);
  if (!claims) {
    removeStateNonce();
    return { success: false, redirectPath: "/", error: "Token invalid or expired" };
  }

  setTokenCookie(token);
  removeStateNonce();
  resetHandoffGuard();

  // Prefer the round-trip `state`, then sessionStorage, then a safe default.
  // In skyagent mode the `state` is the nonce, not the return path — so skip it.
  let returnPath = "";
  if (getAuthMode() !== "skyagent") {
    returnPath = hashParams.state || "";
  }
  if (!isValidRedirectUrl(returnPath)) {
    try {
      returnPath = sessionStorage.getItem(AUTH_RETURN_PATH_KEY) ?? "";
    } catch {
      returnPath = "";
    }
  }
  try {
    sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
  } catch {
    /* ignore */
  }

  return {
    success: true,
    redirectPath: isValidRedirectUrl(returnPath) ? returnPath : "/home",
  };
}

/** Read and remove the stored CSRF state nonce. */
function getStateNonce(): string | null {
  try {
    return sessionStorage.getItem(AUTH_STATE_KEY);
  } catch {
    return null;
  }
}

/** Remove the stored CSRF state nonce (called on both success and failure). */
function removeStateNonce(): void {
  try {
    sessionStorage.removeItem(AUTH_STATE_KEY);
  } catch {
    /* ignore */
  }
}

/** Clear local auth state. Mode-specific: simple → /login form, auth0 → Auth0 logout,
 *  skyagent → SkyAgent root, disabled → home. */
export function logout(): void {
  if (typeof window === "undefined") return;
  clearTokenCookie();
  try {
    sessionStorage.clear();
  } catch {
    /* ignore */
  }
  // Simple mode has no IdP session to end — just return to the login form.
  if (getAuthMode() === "simple") {
    window.location.href = "/login";
    return;
  }
  // SkyAgent mode: local logout only — bounce to the SkyAgent root.
  // If VITE_SKYAGENT_URL is empty, fall back to "/" to avoid a broken redirect.
  if (getAuthMode() === "skyagent") {
    window.location.href = SKYAGENT_URL || "/";
    return;
  }
  const returnTo = encodeURIComponent(window.location.origin);
  if (AUTH0_DOMAIN && AUTH0_CLIENT_ID) {
    window.location.href =
      `https://${AUTH0_DOMAIN}/v2/logout?client_id=${AUTH0_CLIENT_ID}&returnTo=${returnTo}`;
  } else {
    window.location.href = "/";
  }
}
